import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSessionStorage } from 'react-use'
import clsx from 'clsx'

import { SimpleInput, Tabs } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { BuilderComponentGroup, DEFAULT_COMPONENTS_GROUPS } from '@/shared/builder'
import { DEFAULT_DESIGN_BLOCKS_GROUPS, DEFAULT_PAGE_GROUPS } from '@/shared/builder/design-blocks'
import { STORAGE_KEYS } from '@/shared/constants'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'

import { DraggableComponent } from './DraggableComponent'

function filterMap<T>(
  arr: T[],
  filterFn: null | ((item: T, i: number) => boolean) = null,
  mapFn: null | ((item: T, i: number) => unknown) = null
) {
  return arr.reduce((acc, item, i) => {
    if (typeof filterFn === 'function' && !filterFn(item, i)) return acc
    const newItem = typeof mapFn === 'function' ? mapFn(item, i) : item
    return [...acc, newItem]
  }, [] as unknown[])
}

const builderGroupMap: Record<string, BuilderComponentGroup[]> = {
  ['components']: DEFAULT_COMPONENTS_GROUPS,
  'design-blocks': DEFAULT_DESIGN_BLOCKS_GROUPS,
  ['pages']: DEFAULT_PAGE_GROUPS,
}

type GroupType = keyof typeof builderGroupMap

/**
 * ComponentDnd is a function component that provides an interface for searching and selecting components in a builder.
 * It uses context from useComponentDnd to manage the state of the components.
 */
function ComponentDnd() {
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<GroupType>(Object.keys(builderGroupMap)[0])

  const searchTermLower = search.toLowerCase().trim()

  const { activeComponent, activeLayerTableId } = useActiveLayerContext()

  const [activeCompAccordion, setActiveCompAccordion] = useSessionStorage<string[]>(
    STORAGE_KEYS.COMP_ACC_MENU,
    DEFAULT_COMPONENTS_GROUPS.map((c) => c.id)
  )

  // update the list of active accordion components (`activeCompAccordion`)
  const updateActiveCompAccordion = (itemId: string, nextActive: boolean) => {
    setActiveCompAccordion(
      nextActive ? [...activeCompAccordion, itemId] : activeCompAccordion.filter((p) => p !== itemId)
    )
  }
  // compute `filteredComponents` based on the `search` term
  const filteredComponentGroups = useMemo(() => {
    const selectedGroup = builderGroupMap[activeTab]

    if (searchTermLower) {
      return filterMap(
        selectedGroup,
        (c) => {
          return c.components
            .filter((cc) => {
              if (cc.isDetailPageComponent && !activeLayerTableId) return false
              return true
            })
            .some((cc) => {
              const titleMatch = [cc.title.toLowerCase(), cc.compId].join(' ').includes(searchTermLower)
              const keywordMatch =
                cc.searchKeywords?.some((keyword) => keyword.toLowerCase().includes(searchTermLower)) || false
              return titleMatch || keywordMatch
            })
        },
        (c) => {
          return {
            ...c,
            components: c.components
              .filter((cc) => {
                if (cc.isDetailPageComponent && !activeLayerTableId) return false
                return true
              })
              .filter((cc) => {
                const titleMatch = [cc.title.toLowerCase(), cc.compId].join(' ').includes(searchTermLower)
                const keywordMatch =
                  cc.searchKeywords?.some((keyword) => keyword.toLowerCase().includes(searchTermLower)) || false
                return titleMatch || keywordMatch
              }),
          }
        }
      ) as BuilderComponentGroup[]
    } else {
      return selectedGroup
    }
  }, [activeLayerTableId, activeTab, searchTermLower])

  return (
    <div className={clsx('component-panel mk-builder-panel', activeComponent && 'hidden')}>
      <div className='component-filter'>
        <div className='component-search w-full'>
          <SimpleInput
            id='component-search-input'
            onChange={(e) => {
              setSearch(e.currentTarget.value)
            }}
            inputClassName='w-full! txt'
            placeholder={t('Search')}
            outlined
            value={search}
            showClear={Boolean(search)}
            onClear={() => setSearch('')}
          />
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key as any)}
        defaultActiveKey={activeTab}
        className='px-0 mk-builder-tab'
      >
        <Tabs.Nav>
          <Tabs.NavItem eventKey='components'>
            <Trans>Components</Trans>
          </Tabs.NavItem>
          <Tabs.NavItem eventKey='design-blocks'>
            <Trans>Design Blocks</Trans>
          </Tabs.NavItem>
          <Tabs.NavItem eventKey='pages'>
            <Trans>Pages</Trans>
          </Tabs.NavItem>
        </Tabs.Nav>

        <div className=''>
          <Tabs.Panel eventKey='components' className='p-0!'>
            <Accordion onSelect={updateActiveCompAccordion} initialActiveItems={activeCompAccordion}>
              {filteredComponentGroups.map((group) => {
                const open = activeCompAccordion.includes(group.id)
                return (
                  <AccordionItem itemId={group.id} key={group.id} title={<Trans>{group.title}</Trans>}>
                    {open && (
                      <div className='grid grid-cols-3 gap-4'>
                        {group.components.map((comp, i) => {
                          return activeLayerTableId &&
                            comp.isDetailPageComponent === false ? null : comp.isDetailPageComponent &&
                            !activeLayerTableId ? null : (
                            <DraggableComponent
                              key={`${group.id}-${comp.compId}-${i}`}
                              comp={comp}
                              groupId={group.id}
                              type='components'
                            />
                          )
                        })}
                      </div>
                    )}
                  </AccordionItem>
                )
              })}
            </Accordion>
          </Tabs.Panel>

          <Tabs.Panel eventKey='design-blocks' className='p-0!'>
            <Accordion onSelect={updateActiveCompAccordion} initialActiveItems={activeCompAccordion}>
              {filteredComponentGroups.map((group) => {
                const open = activeCompAccordion.includes(group.id)
                return (
                  <AccordionItem itemId={group.id} key={group.id} title={<Trans>{group.title}</Trans>}>
                    {open && (
                      <div className='grid grid-cols-3 gap-4'>
                        {group.components.map((comp, i) => {
                          return comp.isDetailPageComponent && !activeLayerTableId ? null : (
                            <DraggableComponent
                              key={`${group.id}-${comp.compId}-${i}`}
                              comp={comp}
                              groupId={group.id}
                              type='design-blocks'
                            />
                          )
                        })}
                      </div>
                    )}
                  </AccordionItem>
                )
              })}
            </Accordion>
          </Tabs.Panel>

          <Tabs.Panel eventKey='pages' className='p-0!'>
            <Accordion onSelect={updateActiveCompAccordion} initialActiveItems={activeCompAccordion}>
              {filteredComponentGroups.map((group) => {
                const open = activeCompAccordion.includes(group.id)
                return (
                  <AccordionItem itemId={group.id} key={group.id} title={<Trans>{group.title}</Trans>}>
                    {open && (
                      <div className='grid grid-cols-3 gap-4'>
                        {group.components.map((comp, i) => {
                          return comp.isDetailPageComponent && !activeLayerTableId ? null : (
                            <DraggableComponent
                              key={`${group.id}-${comp.compId}-${i}`}
                              comp={comp}
                              groupId={group.id}
                              type='pages'
                            />
                          )
                        })}
                      </div>
                    )}
                  </AccordionItem>
                )
              })}
            </Accordion>
          </Tabs.Panel>
        </div>
      </Tabs>
    </div>
  )
}

export default ComponentDnd
